import type { SharedRecord, SharedRecordStatus } from '../../api/sharedRecordReviewClient';

const STATUS_PILL: Record<SharedRecordStatus, string> = {
  Proposed: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  Flagged: 'bg-red-500/15 text-red-300 border-red-500/30',
};

function sourceHost(url: string | null): string {
  if (!url) return 'No source cited';
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

interface Props {
  records: SharedRecord[];
  selectedId: string | null;
  onSelect: (recordId: string) => void;
}

export function SharedRecordReviewTable({ records, selectedId, onSelect }: Props) {
  return (
    <div className="overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900">
      <table className="w-full text-left text-sm" aria-label="Shared records waiting for review">
        <thead className="border-b border-zinc-800 text-[11px] uppercase tracking-wide text-zinc-500">
          <tr>
            <th scope="col" className="px-3 py-2 font-medium">Record</th>
            <th scope="col" className="px-3 py-2 font-medium">Value</th>
            <th scope="col" className="px-3 py-2 font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {records.map((r) => {
            const selected = r.recordId === selectedId;
            return (
              <tr
                key={r.recordId}
                tabIndex={0}
                aria-current={selected ? 'true' : undefined}
                onClick={() => onSelect(r.recordId)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect(r.recordId);
                  }
                }}
                className={`min-h-[48px] cursor-pointer border-b border-zinc-800 last:border-b-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 ${
                  selected ? 'bg-violet-500/10' : 'hover:bg-zinc-800/60'
                }`}
              >
                <td className="px-3 py-3">
                  <div className="font-medium text-zinc-100">{r.key}</div>
                  <div className="text-xs text-zinc-500">
                    {r.category} · {sourceHost(r.sourceUrl)}
                  </div>
                </td>
                <td className="px-3 py-3 text-zinc-300">{r.storedValue ?? '—'}</td>
                <td className="px-3 py-3">
                  <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${STATUS_PILL[r.status]}`}>
                    {r.status}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
